import React, { useRef, useState, useEffect } from "react";
import { graphql } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";
import { withDefault, QueryParamConfig } from "serialize-query-params";

import routes from "../../clhbid-routes";

import downArrow from "../images/angle-double-down-solid.svg";

import useQueryParams from "../hooks/use-query-params";
import useSaleStatuses from "../hooks/use-sale-statuses";

import Button from "../components/Button";
import SaleDate from "../components/SaleDate";
import PlayButton from "../components/PlayButton";
import SaleSummary from "../components/SaleSummary";
import MediaGallery from "../components/MediaGallery";
import SaleCountdownButton from "../components/SaleCountdownButton";

const BooleanParam: QueryParamConfig<boolean> = {
  encode: (value: boolean) => {
    return value ? "true" : "false";
  },
  decode: (value: string) => {
    if (typeof value !== "string") return undefined;
    if (value.toLowerCase() === "true") return true;
    if (value.toLowerCase() === "false") return false;
    return undefined;
  },
};

const SaleOverview: React.FC<{
  sale: Queries.SaleOverviewSaleFragment;
}> = ({ sale }) => {
  const wrapper = useRef();
  const [src, setSrc] = useState(undefined);
  const { [sale.slug]: saleStatus } = useSaleStatuses();

  const [{ video: isTourVideoOpen }, setParams] = useQueryParams({
    video: withDefault(BooleanParam, false),
  });

  // As the width of the window changes, update the video source to match
  useEffect(() => {
    const getUrl = () => {
      const newSrc =
        window.innerWidth > 1024
          ? sale.trailerVideoDesktopUrl
          : sale.trailerVideoMobileUrl;
      if (src !== newSrc) setSrc(newSrc);
    };

    getUrl();

    window.addEventListener("resize", getUrl);

    return () => {
      window.removeEventListener("resize", getUrl);
    };
  }, [src, sale]);

  // Display the background video if it's available, and the main video isn't playing
  // Playing both videos at the same time causes performance issues on lower-end devices
  const isTourVideoAvailable = src && !isTourVideoOpen;

  // If the tour video is availabe, then play it back after a delay
  // If the video source has changed then play it back after a timeout
  const backgroundSaleVideoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    console.log("Registering sale video playback timeout");
    const cancelPlaybackID = setTimeout(() => {
      if (backgroundSaleVideoRef.current) {
        console.log("Playing sale video");
        backgroundSaleVideoRef.current.play().catch(() => {
          console.log(
            "Failed to play sale video. This is likely due to power settings. Ignoring as we don't care!"
          );
        });
      }
    }, 2000);

    return () => {
      console.log("Cancelling sale video playback");
      clearTimeout(cancelPlaybackID);
    };
  }, [isTourVideoAvailable, src]);

  return (
    <div id="overview" className="mb-16">
      <MediaGallery
        open={isTourVideoOpen}
        multiple={false}
        media={{
          __typename: "ContentfulVimeoVideo",
          title: `Tour ${sale.name}`,
          description: `Tour ${sale.name}`,
          url: sale.tourVideoUrl,
        }}
        handleClose={() => history.back()}
      />
      <div className="relative pb-20 lg:pb-14 lg:pt-8">
        <div className="absolute inset-0 z-0 h-full overflow-hidden">
          {isTourVideoAvailable && (
            <video
              loop
              muted
              playsInline
              controls={false}
              poster={sale.saleImage.url}
              className="h-full w-full bg-cover object-cover"
              src={src}
              ref={backgroundSaleVideoRef}
            ></video>
          )}
          {!isTourVideoAvailable && (
            <GatsbyImage
              alt=""
              loading="eager"
              image={sale.saleImage.gatsbyImageData}
              className="h-full w-full object-cover"
            />
          )}
          <div className="absolute inset-0 z-10 bg-black/70" />
        </div>
        <div className="relative mx-auto max-w-[1200px] px-4 pt-16 lg:flex lg:flex-row-reverse lg:items-center lg:pt-0">
          <button
            disabled={!sale.tourVideoUrl}
            onClick={() => setParams({ video: true }, "push")}
            className="mx-auto mb-16 flex flex-col items-center lg:my-0"
          >
            <PlayButton className={!sale.tourVideoUrl ? "opacity-30" : ""} />
            <div className="mt-2 max-w-xs truncate font-semibold text-white">
              Tour {sale.tourVideoUrl ? sale.name : "Coming Soon"}
            </div>
          </button>
          <div className="lg:w-[640px] lg:rounded-lg lg:bg-black/70 lg:p-6">
            <h1 className="mb-4 text-3xl font-bold text-clhbid-orange lg:mb-1 lg:text-4xl">
              {sale.name}
            </h1>
            <SaleDate
              showLive
              type="long"
              sale={sale}
              className="mb-6"
              dateClassName="text-white font-bold text-xl"
              subtextClassName="text-white font-normal text-base"
              prefixClassName="text-clhbid-orange font-bold text-xl italic"
              liveClassName="text-white font-bold text-xl italic"
            />
            <SaleSummary sale={sale} className="mb-16 lg:mb-6" />
            <p className="hidden font-semibold text-white lg:block">
              {sale.summary.summary}
            </p>
            <div ref={wrapper} className="flex flex-col gap-4 lg:flex-row">
              <SaleCountdownButton sale={sale} />
              <Button
                outline
                href={routes.sale.reminder(sale)}
                state={{ modal: true, noScroll: true }}
                disabled={saleStatus === "live" || saleStatus === "completed"}
              >
                Set Reminder
              </Button>
            </div>
          </div>
        </div>
        <svg
          viewBox="0 0 400 32"
          preserveAspectRatio="none"
          className="absolute bottom-0 block h-8 w-full"
        >
          <path
            d="M400 0V32H202H0V0L202 32L400 0Z"
            className="fill-clhbid-charcoal"
          />
        </svg>
        <div className="absolute -bottom-8 flex w-full items-center justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white shadow-lg">
            <img alt="" src={downArrow} className="h-8" />
          </div>
        </div>
      </div>
      <div>
        <p className="m-0 mt-16 px-4 text-lg font-semibold lg:hidden">
          {sale.summary.summary}
        </p>
      </div>
    </div>
  );
};

export const SaleOverviewFragment = graphql`
  fragment SaleOverviewSale on ContentfulSale {
    name
    slug
    banner
    location
    saleStatus
    tourVideoUrl
    trailerVideoMobileUrl
    trailerVideoDesktopUrl
    summary {
      summary
    }
    parcels {
      size
      acceptedBid
      startingBid
      enBloc
    }
    saleImage {
      url
      gatsbyImageData
    }
    ...SaleDateSale
    ...SaleCountdownButtonSale
  }
`;

export default SaleOverview;
